import React, { useEffect, useState } from "react";
import { ChevronLeft, RotateCw } from "lucide-react";
import MatchCard from "../components/ui/MatchCard";
import TournamentResultModal from "../components/modals/TournamentResultModal";
import { useTournaments } from "../hooks";

interface TournamentBracketViewProps {
  tournamentName: string;
  eventId: string;
  onBack: () => void;
  isAdmin: boolean;
  isEventEnded: boolean;
}

const TournamentBracketView: React.FC<TournamentBracketViewProps> = ({
  tournamentName,
  eventId,
  onBack,
  isAdmin,
  isEventEnded,
}) => {
  const {
    matches,
    isLoading,
    error,
    fetchTournament,
    registerTournamentResult,
  } = useTournaments();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    round?: number;
    blockNumber?: number;
    p1TournamentId?: string;
    p2TournamentId?: string;
    p1Name?: string;
    p2Name?: string;
    p1Wins?: number | null;
    p2Wins?: number | null;
  }>({ isOpen: false });
  const [isReloading, setIsReloading] = useState(false);
  const CARD_HEIGHT = 104;
  const BASE_GAP = 24;
  const COLUMN_GAP = 56;
  const CONNECTOR_WIDTH = COLUMN_GAP / 2;
  const canRegisterResult = isAdmin && !isEventEnded;
  const roundColumns = matches.reduce<Record<number, typeof matches>>(
    (acc, match) => {
      if (!acc[match.round]) acc[match.round] = [];
      acc[match.round].push(match);
      return acc;
    },
    {},
  );

  const rounds = Object.keys(roundColumns)
    .map(Number)
    .sort((a, b) => b - a);

  const getExpectedMatchCount = (round: number): number =>
    Math.max(1, Math.floor(round / 2));

  const getLayoutMatchCount = (round: number): number =>
    Math.max(roundColumns[round]?.length ?? 0, getExpectedMatchCount(round));

  const normalizePx = (value: number): number => Math.round(value);

  const roundLayouts = rounds.reduce<
    Record<number, { gap: number; marginTop: number; step: number }>
  >((acc, round, index) => {
    if (index === 0) {
      acc[round] = {
        gap: BASE_GAP,
        marginTop: 0,
        step: CARD_HEIGHT + BASE_GAP,
      };
      return acc;
    }

    const prevRound = rounds[index - 1];
    const prevCount = getLayoutMatchCount(prevRound);
    const currentCount = getLayoutMatchCount(round);
    const ratio = Math.max(1, prevCount / currentCount);
    const prevStep = acc[prevRound].step;
    const currentStep = normalizePx(prevStep * ratio);
    const currentMarginTop = normalizePx(
      acc[prevRound].marginTop + (currentStep - prevStep) / 2,
    );

    acc[round] = {
      gap: normalizePx(Math.max(0, currentStep - CARD_HEIGHT)),
      marginTop: currentMarginTop,
      step: currentStep,
    };

    return acc;
  }, {});

  const getRoundLabel = (round: number): string => {
    if (round === 4) return "Semifinal";
    if (round === 2) return "Final";
    return `Round ${round}`;
  };

  useEffect(() => {
    fetchTournament(eventId);
  }, [eventId, fetchTournament]);

  useEffect(() => {
    if (isEventEnded) {
      setModalState({ isOpen: false });
    }
  }, [isEventEnded]);

  const handleReload = async () => {
    setIsReloading(true);
    await fetchTournament(eventId, { nocache: true });
    setIsReloading(false);
  };

  return (
    <div className="flex flex-col h-full min-h-viewport bg-[#f8fafc] animate-in slide-in-from-right-8 duration-500 overflow-hidden">
      <header className="px-6 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 flex items-center justify-between border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-20 text-left shrink-0">
        <div className="flex items-center gap-4 text-left min-w-0 w-full">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center bg-white shadow-sm active:scale-90"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h2 className="flex-1 min-w-0 font-black text-slate-900 uppercase text-xs tracking-wide text-left line-clamp-2 leading-snug">
            {tournamentName}
          </h2>
          <button
            onClick={handleReload}
            disabled={isReloading}
            className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center bg-white shadow-sm active:scale-90 disabled:opacity-50"
          >
            <RotateCw
              className={`w-5 h-5 text-slate-600 ${
                isReloading ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-x-auto flex items-start gap-12 px-8 py-12 scrollbar-hide pb-40">
        {isLoading && (
          <p className="text-slate-400 text-sm m-auto">読み込み中...</p>
        )}
        {error && <p className="text-rose-400 text-sm m-auto">{error}</p>}
        {!isLoading && !error && matches.length === 0 && (
          <p className="text-slate-300 text-sm m-auto">
            試合データがありません
          </p>
        )}
        {!isLoading && !error && matches.length > 0 && (
          <div
            className="flex items-start shrink-0 text-left"
            style={{ gap: `${COLUMN_GAP}px` }}
          >
            {(() => {
              const finalMatch = matches.find((m) => m.round === 2);
              const hasFinalResult =
                finalMatch != null &&
                finalMatch.p1Wins !== null &&
                finalMatch.p2Wins !== null;
              return (
                <>
                  {rounds.map((round, roundIndex) => (
                    <div
                      key={round}
                      className="flex flex-col gap-3 shrink-0 relative"
                    >
                      {round === 2 ? (
                        <p className="text-[11px] font-black uppercase tracking-widest pl-1 text-amber-500 flex items-center gap-1">
                          🏆 {getRoundLabel(round)}
                        </p>
                      ) : (
                        <p className="text-[10px] font-black uppercase tracking-widest pl-1 text-slate-400">
                          {getRoundLabel(round)}
                        </p>
                      )}
                      <div
                        className="flex flex-col relative"
                        style={{
                          gap: `${roundLayouts[round]?.gap ?? BASE_GAP}px`,
                          marginTop: `${roundLayouts[round]?.marginTop ?? 0}px`,
                        }}
                      >
                        {Array.from(
                          { length: getExpectedMatchCount(round) },
                          (_, slotIndex) => {
                            const match = roundColumns[round].find(
                              (m) => m.blockNumber === slotIndex + 1,
                            );
                            const hasNextRound = roundIndex < rounds.length - 1;
                            const isPairStart = slotIndex % 2 === 0;
                            const hasPair =
                              slotIndex + 1 < getExpectedMatchCount(round);

                            if (!match) {
                              return (
                                <div
                                  key={`${round}-slot-${slotIndex}`}
                                  className="relative"
                                  style={{ height: `${CARD_HEIGHT}px` }}
                                />
                              );
                            }

                            return (
                              <div
                                key={`${match.round}-${match.blockNumber}`}
                                className="relative"
                              >
                                <MatchCard
                                  p1={match.p1Name}
                                  p2={match.p2Name}
                                  s1={match.p1Wins}
                                  s2={match.p2Wins}
                                  isLive={false}
                                  matchNum={match.blockNumber}
                                  isAdmin={canRegisterResult}
                                  isFinalRound={round === 2}
                                  round={match.round}
                                  blockNumber={match.blockNumber}
                                  onResultClick={(
                                    round,
                                    blockNumber,
                                    p1Name,
                                    p2Name,
                                    p1Wins,
                                    p2Wins,
                                  ) => {
                                    if (!canRegisterResult) return;
                                    setModalState({
                                      isOpen: true,
                                      round,
                                      blockNumber,
                                      p1TournamentId: match.p1TournamentId,
                                      p2TournamentId: match.p2TournamentId,
                                      p1Name,
                                      p2Name,
                                      p1Wins,
                                      p2Wins,
                                    });
                                  }}
                                />

                                {hasNextRound && (
                                  <div
                                    className="absolute border-t border-slate-300"
                                    style={{
                                      top: "50%",
                                      right: `${-CONNECTOR_WIDTH}px`,
                                      width: `${CONNECTOR_WIDTH}px`,
                                      transform: "translateY(-50%)",
                                    }}
                                  />
                                )}

                                {roundIndex > 0 && (
                                  <div
                                    className="absolute border-t border-slate-300"
                                    style={{
                                      top: "50%",
                                      left: `${-CONNECTOR_WIDTH}px`,
                                      width: `${CONNECTOR_WIDTH}px`,
                                      transform: "translateY(-50%)",
                                    }}
                                  />
                                )}

                                {hasNextRound && isPairStart && hasPair && (
                                  <div
                                    className="absolute border-r border-slate-300"
                                    style={{
                                      top: "50%",
                                      right: `${-CONNECTOR_WIDTH}px`,
                                      height: `calc(100% + ${roundLayouts[round].gap}px)`,
                                    }}
                                  />
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Results column: 1位・2位 */}
                  {hasFinalResult &&
                    (() => {
                      const finalMatch = matches.find((m) => m.round === 2)!;
                      const p1Won =
                        (finalMatch.p1Wins ?? 0) > (finalMatch.p2Wins ?? 0);
                      const winner = p1Won
                        ? finalMatch.p1Name
                        : finalMatch.p2Name;
                      const runnerUp = p1Won
                        ? finalMatch.p2Name
                        : finalMatch.p1Name;
                      const finalLayout = roundLayouts[2];
                      const RESULT_CARD_HEIGHT = 68;
                      const resultsTotalHeight =
                        RESULT_CARD_HEIGHT * 2 + BASE_GAP;
                      const finalCardCenter =
                        (finalLayout?.marginTop ?? 0) + CARD_HEIGHT / 2;
                      const resultsMarginTop = Math.max(
                        0,
                        finalCardCenter - resultsTotalHeight / 2,
                      );
                      return (
                        <div className="flex flex-col gap-3 shrink-0 relative">
                          <p className="text-[11px] font-black uppercase tracking-widest pl-1 text-amber-500">
                            順位
                          </p>
                          <div
                            className="flex flex-col relative"
                            style={{
                              gap: `${BASE_GAP}px`,
                              marginTop: `${resultsMarginTop}px`,
                            }}
                          >
                            {/* 1位 */}
                            <div
                              className="rounded-lg bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-50 border-2 border-amber-400 px-4 flex items-center gap-3 shadow-md"
                              style={{
                                width: "208px",
                                height: `${RESULT_CARD_HEIGHT}px`,
                              }}
                            >
                              <span className="text-3xl leading-none select-none">
                                🥇
                              </span>
                              <div className="min-w-0">
                                <p className="text-[9px] font-black uppercase tracking-widest text-amber-500 mb-0.5">
                                  1位
                                </p>
                                <p className="text-sm font-black text-amber-800 truncate">
                                  {winner}
                                </p>
                              </div>
                            </div>

                            {/* 2位 */}
                            <div
                              className="rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-300 px-4 flex items-center gap-3 shadow-sm"
                              style={{
                                width: "208px",
                                height: `${RESULT_CARD_HEIGHT}px`,
                              }}
                            >
                              <span className="text-3xl leading-none select-none">
                                🥈
                              </span>
                              <div className="min-w-0">
                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">
                                  2位
                                </p>
                                <p className="text-sm font-black text-slate-600 truncate">
                                  {runnerUp}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Tournament Result Modal */}
      {canRegisterResult && (
        <TournamentResultModal
          isOpen={modalState.isOpen}
          onClose={() => setModalState({ isOpen: false })}
          p1Name={modalState.p1Name ?? ""}
          p2Name={modalState.p2Name ?? ""}
          p1Wins={modalState.p1Wins ?? null}
          p2Wins={modalState.p2Wins ?? null}
          onSubmit={async (p1Wins, p2Wins) => {
            if (!modalState.p1TournamentId || !modalState.p2TournamentId) {
              console.error("Tournament IDs not found for selected match");
              return;
            }
            await registerTournamentResult(
              eventId,
              modalState.p1TournamentId,
              modalState.p2TournamentId,
              p1Wins,
              p2Wins,
            );
          }}
        />
      )}
    </div>
  );
};

export default TournamentBracketView;
